import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import BrandLogo from "./BrandLogo";
import TopBarTools from "./TopBarTools";
import DiploksLiveBalanceWidget from "./DiploksLiveBalanceWidget";
import WelcomeSplash from "./WelcomeSplash";
import SmartSupportPanel from "./SmartSupportPanel";
import GraceModeBanner from "./GraceModeBanner";
import { recordVisit } from "../lib/nav-history";
import {
  LayoutDashboard,
  CalendarRange,
  Users,
  Flag,
  BookOpen,
  Radio,
  Award,
  Gavel,
  Megaphone,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  ScrollText,
  MessageCircle,
  FileCheck2,
  GraduationCap,
  School,
  UserCheck,
  ClipboardList,
  Heart,
  ShieldCheck,
  Trophy,
  IdCard,
  Sparkles,
  Vote,
  LifeBuoy,
  AlertTriangle,
  Briefcase,
  Send,
  Receipt,
  TrendingUp,
  Activity,
  Lightbulb,
  FileText,
  Coins,
  Zap,
  Gift,
  Network,
  ScanLine,
  ChevronDown,
  Rocket,
  Wand2,
  PenTool,
  KeyRound,
  Globe,
} from "lucide-react";

// ─── Sidebar configuration ──────────────────────────────────────────────
// Shape per role:
//   {
//     top:    [{...item}],                          // ALWAYS visible, no group header
//     groups: [{ id, label, items: [{...item}] }],  // collapsible sections
//   }
//
// Each item is { to, label, icon }.  Use IDs (kebab-case) on groups —
// they double as localStorage keys for remembered collapse state.
// Exported so the global ⌘K search palette (TopBarTools.jsx) can
// surface "navigate to <page>" results alongside content matches.
export const NAV_BY_ROLE = {
  super_admin: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "platform",
        label: "Platform",
        headerIcon: Network,
        items: [
          { to: "/dashboard/organizations", label: "Organizations", icon: Building2 },
          { to: "/dashboard/watchdog", label: "Watchdog", icon: Activity },
          { to: "/dashboard/audit", label: "Audit Logs", icon: ShieldCheck },
          { to: "/dashboard/gateway-review", label: "Gateway Review", icon: ShieldCheck },
          { to: "/dashboard/rbac/advanced", label: "Advanced RBAC", icon: Network },
          { to: "/dashboard/admin/email-templates", label: "Email Templates", icon: Send },
          { to: "/dashboard/admin/ai-ux-preview", label: "Intelligence Preview", icon: Sparkles },
        ],
      },
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "All Events", icon: CalendarRange },
          { to: "/dashboard/polls", label: "Polls & Crisis", icon: Vote },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
          { to: "/dashboard/invites", label: "Invite Tracking", icon: Send },
        ],
      },
      {
        id: "marketing",
        label: "Marketing & Analytics",
        headerIcon: Rocket,
        items: [
          { to: "/dashboard/automation", label: "Automation", icon: Megaphone },
          { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
        ],
      },
      {
        id: "lms",
        label: "LMS & Live Classroom",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms-analytics", label: "LMS Analytics", icon: GraduationCap },
          { to: "/dashboard/lms/admin/course-builder", label: "Course Builder", icon: BookOpen },
          { to: "/dashboard/lms", label: "Learning Academy", icon: Sparkles },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
          { to: "/dashboard/lms/live-sessions/scheduler", label: "Schedule Live", icon: CalendarRange },
          { to: "/dashboard/lms/live-analytics", label: "Live Analytics", icon: Activity },
          { to: "/dashboard/lms/admin/live-provider-settings", label: "Live Provider", icon: Settings },
          { to: "/dashboard/trainer/grading-queue", label: "Grading Queue", icon: ClipboardList },
        ],
      },
      {
        id: "access",
        label: "Access & Gamification",
        headerIcon: KeyRound,
        items: [
          { to: "/dashboard/admin/access-control", label: "Access Control", icon: ScanLine },
          { to: "/dashboard/admin/podium-rules", label: "Podium Rules", icon: Trophy },
          { to: "/dashboard/admin/goodwill-audit", label: "Goodwill Audit", icon: ShieldCheck },
        ],
      },
      {
        id: "diploks",
        label: "Diploks",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/admin/diploks", label: "Diploks Admin", icon: Coins },
          { to: "/dashboard/admin/diploks/reconciliation", label: "Reconciliation", icon: AlertTriangle },
          { to: "/dashboard/admin/diploks/adjustments", label: "Adjustments", icon: ShieldCheck },
          { to: "/dashboard/admin/diploks/vouchers-promos", label: "Vouchers & Promos", icon: Gift },
          { to: "/dashboard/admin/diploks/feature-costs", label: "Feature Costs", icon: Coins },
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
          { to: "/dashboard/diploks/organiser/grants", label: "Grant Diploks", icon: Gift },
        ],
      },
    ],
  },

  org_admin: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/events/new", label: "New event", icon: Gavel },
          { to: "/dashboard/polls", label: "Polls & Crisis", icon: Vote },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
          { to: "/dashboard/invites", label: "Invite Tracking", icon: Send },
        ],
      },
      {
        id: "growth",
        label: "Marketing & Growth",
        headerIcon: Rocket,
        items: [
          { to: "/dashboard/crm", label: "Delegate CRM", icon: MessageCircle },
          { to: "/dashboard/automation", label: "Automation", icon: Megaphone },
          { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
          { to: "/dashboard/revenue", label: "Revenue", icon: TrendingUp },
        ],
      },
      {
        id: "lms",
        label: "LMS & Live Classroom",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms-analytics", label: "LMS Analytics", icon: GraduationCap },
          { to: "/dashboard/lms/admin/course-builder", label: "Course Builder", icon: BookOpen },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
          { to: "/dashboard/lms/live-sessions/scheduler", label: "Schedule Live", icon: CalendarRange },
          { to: "/dashboard/lms/live-analytics", label: "Live Analytics", icon: Activity },
          { to: "/dashboard/trainer/grading-queue", label: "Grading Queue", icon: ClipboardList },
          { to: "/dashboard/admin/podium-rules", label: "Podium Rules", icon: Trophy },
        ],
      },
      {
        id: "access",
        label: "Access Control",
        headerIcon: KeyRound,
        items: [
          { to: "/dashboard/admin/access-control", label: "Access Control", icon: ScanLine },
          { to: "/dashboard/access/scanner", label: "Scan AccessPass", icon: ScanLine },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/wallet", label: "Org Diploks Wallet", icon: Coins },
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
          { to: "/dashboard/diploks/organiser/grants", label: "Grant Diploks", icon: Gift },
        ],
      },
      {
        id: "admin",
        label: "Admin",
        headerIcon: ShieldCheck,
        items: [
          { to: "/dashboard/rbac/advanced", label: "Advanced RBAC", icon: Network },
          { to: "/dashboard/gateway-review", label: "Gateway Review", icon: ShieldCheck },
          { to: "/dashboard/settings", label: "Branding", icon: Settings },
        ],
      },
    ],
  },

  event_manager: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/events/new", label: "New event", icon: Gavel },
          { to: "/dashboard/polls", label: "Polls & Crisis", icon: Vote },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
      {
        id: "growth",
        label: "Marketing & Analytics",
        headerIcon: Rocket,
        items: [
          { to: "/dashboard/crm", label: "Delegates CRM", icon: MessageCircle },
          { to: "/dashboard/automation", label: "Automation", icon: Megaphone },
          { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
        ],
      },
      {
        id: "lms",
        label: "LMS & Live Classroom",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms/admin/course-builder", label: "Course Builder", icon: BookOpen },
          { to: "/dashboard/lms/trainer", label: "Trainer Console", icon: GraduationCap },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
          { to: "/dashboard/lms/live-sessions/scheduler", label: "Schedule Live", icon: CalendarRange },
          { to: "/dashboard/lms/live-analytics", label: "Live Analytics", icon: Activity },
          { to: "/dashboard/trainer/grading-queue", label: "Grading Queue", icon: ClipboardList },
          { to: "/dashboard/admin/podium-rules", label: "Podium Rules", icon: Trophy },
        ],
      },
      {
        id: "access",
        label: "Access Control",
        headerIcon: KeyRound,
        items: [
          { to: "/dashboard/admin/access-control", label: "Access Control", icon: ScanLine },
          { to: "/dashboard/access/scanner", label: "Scan AccessPass", icon: ScanLine },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/wallet", label: "Org Diploks Wallet", icon: Coins },
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
          { to: "/dashboard/diploks/organiser/grants", label: "Grant Diploks", icon: Gift },
        ],
      },
    ],
  },

  chair: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "committees",
        label: "My Committees",
        headerIcon: Gavel,
        items: [
          { to: "/dashboard/events", label: "My Events", icon: CalendarRange },
          { to: "/dashboard/live", label: "Live Sessions", icon: Radio },
          { to: "/dashboard/polls", label: "Polls & Crisis", icon: Vote },
          { to: "/dashboard/awards", label: "Awards", icon: Award },
          { to: "/dashboard/chair-assist", label: "AI Assistant", icon: Sparkles },
        ],
      },
      {
        id: "learning",
        label: "Training & Grading",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
          { to: "/dashboard/lms/chair", label: "Chair Readiness", icon: Gavel },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
          { to: "/dashboard/lms/live-analytics", label: "Live Analytics", icon: Activity },
          { to: "/dashboard/trainer/grading-queue", label: "Grading Queue", icon: ClipboardList },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
    ],
  },

  school_admin: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "delegation",
        label: "My Delegation",
        headerIcon: Users,
        items: [
          { to: "/dashboard/school/delegates", label: "Our Delegates", icon: Users },
          { to: "/dashboard/school/invoices", label: "Invoices", icon: ScrollText },
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
        ],
      },
      {
        id: "learning",
        label: "Learning",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
          { to: "/dashboard/lms/faculty", label: "School Readiness", icon: School },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/wallet", label: "School Diploks Wallet", icon: Coins },
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
          { to: "/dashboard/diploks/organiser/grants", label: "Grant Diploks", icon: Gift },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
    ],
  },

  faculty_advisor: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "delegation",
        label: "My Delegation",
        headerIcon: Users,
        items: [
          { to: "/dashboard/school/delegates", label: "Students", icon: Users },
          { to: "/dashboard/faculty/schedule", label: "Schedule", icon: CalendarRange },
          { to: "/dashboard/certificates", label: "Awards", icon: Award },
        ],
      },
      {
        id: "learning",
        label: "Learning & Grading",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
          { to: "/dashboard/lms/faculty", label: "Delegation Readiness", icon: School },
          { to: "/dashboard/trainer/grading-queue", label: "Grading Queue", icon: ClipboardList },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
          { to: "/dashboard/lms/live-analytics", label: "Live Analytics", icon: Activity },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
    ],
  },

  volunteer: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "access",
        label: "Access Control",
        headerIcon: KeyRound,
        items: [
          { to: "/dashboard/volunteer/attendance", label: "Attendance", icon: UserCheck },
          { to: "/dashboard/access/scanner", label: "Scan AccessPass", icon: ScanLine },
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/access-passes", label: "My Pass", icon: IdCard },
        ],
      },
      {
        id: "learning",
        label: "Learning",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
        ],
      },
    ],
  },

  parent: {
    top: [{ to: "/dashboard", label: "My Child", icon: Heart }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/access-passes", label: "My AccessPass", icon: IdCard },
          { to: "/dashboard/certificates", label: "Awards", icon: Award },
        ],
      },
      {
        id: "child",
        label: "Child Tracking",
        headerIcon: Heart,
        items: [
          { to: "/dashboard/lms/parent", label: "Child Progress", icon: GraduationCap },
          { to: "/dashboard/lms/parent/live", label: "Child Live Classes", icon: Radio },
          { to: "/dashboard/diploks/parent/children", label: "Linked Children", icon: Users },
        ],
      },
      {
        id: "diploks",
        label: "Diploks Wallet",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/diploks/auto-topup", label: "Auto Top-Up", icon: Zap },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
        ],
      },
    ],
  },

  delegate: {
    top: [{ to: "/dashboard", label: "Overview", icon: LayoutDashboard }],
    groups: [
      {
        id: "events",
        label: "My Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "My Events", icon: CalendarRange },
          { to: "/dashboard/access-passes", label: "My AccessPass", icon: IdCard },
          { to: "/dashboard/passport", label: "My Passport", icon: IdCard },
          { to: "/dashboard/polls", label: "Live Polls", icon: AlertTriangle },
          { to: "/dashboard/certificates", label: "Certificates", icon: Award },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
      {
        id: "prep",
        label: "AI Prep",
        headerIcon: Wand2,
        items: [
          { to: "/dashboard/ai-prep", label: "AI Prep", icon: Sparkles },
          { to: "/dashboard/lms/mun-prep", label: "MUN Prep Hub", icon: Sparkles },
          { to: "/dashboard/scenarios", label: "Scenario Drills", icon: Lightbulb },
          { to: "/dashboard/sandbox", label: "Resolution Sandbox", icon: FileText },
          { to: "/dashboard/courses", label: "Course Library", icon: BookOpen },
        ],
      },
      {
        id: "live",
        label: "Live Learning",
        headerIcon: Radio,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
          { to: "/dashboard/lms/live-sessions", label: "Live Classroom", icon: Radio },
        ],
      },
      {
        id: "work",
        label: "My Work",
        headerIcon: PenTool,
        items: [
          { to: "/dashboard/papers", label: "Papers", icon: ScrollText },
          { to: "/dashboard/resolutions", label: "Resolutions", icon: Vote },
        ],
      },
      {
        id: "progress",
        label: "Progress",
        headerIcon: Trophy,
        items: [
          { to: "/dashboard/podium-journey", label: "Podium Journey", icon: Trophy },
          { to: "/dashboard/road-to-podium", label: "Road to Podium", icon: Trophy },
        ],
      },
      {
        id: "wallet",
        label: "Wallet & Billing",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/billing", label: "Billing", icon: Receipt },
        ],
      },
      {
        id: "support",
        label: "Support",
        headerIcon: LifeBuoy,
        items: [
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
          { to: "/dashboard/privacy", label: "Privacy", icon: ShieldCheck },
        ],
      },
    ],
  },

  sponsor: {
    top: [{ to: "/dashboard", label: "Sponsor portal", icon: Briefcase }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
        ],
      },
    ],
  },

  judge: {
    top: [{ to: "/dashboard", label: "Scoring desk", icon: Gavel }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/leaderboard", label: "Leaderboard", icon: Trophy },
        ],
      },
      {
        id: "learning",
        label: "Learning",
        headerIcon: GraduationCap,
        items: [
          { to: "/dashboard/lms", label: "Learning Academy", icon: GraduationCap },
        ],
      },
      {
        id: "diploks",
        label: "Diploks & Support",
        headerIcon: Coins,
        items: [
          { to: "/dashboard/diploks/personal/wallet", label: "My Diploks", icon: Coins },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
        ],
      },
    ],
  },

  partner_institution: {
    top: [{ to: "/dashboard", label: "Partner portal", icon: Building2 }],
    groups: [
      {
        id: "events",
        label: "Events",
        headerIcon: Sparkles,
        items: [
          { to: "/dashboard/events", label: "Events", icon: CalendarRange },
          { to: "/dashboard/helpdesk", label: "Helpdesk", icon: LifeBuoy },
        ],
      },
    ],
  },
};

/** Flatten a grouped role-config into a single ordered array of nav
 * items.  Used by the mobile horizontal scroll-strip, the global
 * ⌘K palette in TopBarTools, and the badge-resolver below.  Top
 * items come first, then every group's items in render order. */
export function flattenNav(roleConfig) {
  if (!roleConfig) return [];
  const top = roleConfig.top || [];
  const grouped = (roleConfig.groups || []).flatMap((g) => g.items);
  return [...top, ...grouped];
}


function SideLink({ to, label, Icon, end, badge }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={() => recordVisit(to)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `sidebar-link group relative flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium ${
          isActive
            ? "is-active bg-white/10 text-white"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="sidebar-active-bar absolute left-0 top-1 bottom-1 w-1 rounded-r bg-brand-gold shadow-[0_0_12px_rgba(212,175,55,0.55)]" />
          )}
          <Icon className="sidebar-link-icon h-4 w-4 relative z-10" />
          <span className="relative z-10 flex-1">{label}</span>
          {badge && badge > 0 ? (
            <span
              data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative z-10 ml-auto inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-[0_0_8px_rgba(244,63,94,0.5)] animate-pulse"
            >
              {badge > 99 ? "99+" : badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/**
 * Collapsible sidebar section with a small uppercase header.  Default
 * expanded; collapse-state is persisted per (role, group) under
 * ``localStorage.diplomexa_sidebar_collapsed``.  Clicking the chevron
 * (or anywhere on the header row) toggles it.  The header is
 * keyboard-accessible — Space / Enter toggle, ``aria-expanded`` set.
 */
const SIDEBAR_STORAGE_KEY = "diplomexa_sidebar_collapsed_v1";

function _loadCollapsedSet(role) {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(parsed?.[role] || []);
  } catch {
    return new Set();
  }
}

function _saveCollapsedSet(role, set) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[role] = Array.from(set);
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    /* silent — localStorage might be full or disabled */
  }
}

function SidebarGroup({ groupId, label, headerIcon: HeaderIcon, items, badgeFor, collapsed, onToggle }) {
  // Hide groups that ended up empty (defensive — shouldn't happen).
  if (!items || items.length === 0) return null;
  const headerId = `sidebar-group-header-${groupId}`;
  const listId = `sidebar-group-list-${groupId}`;
  return (
    <div className="mb-3" data-testid={`sidebar-group-${groupId}`}>
      <button
        id={headerId}
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={listId}
        data-testid={`sidebar-group-toggle-${groupId}`}
        className="sidebar-group-header w-full flex items-center justify-between px-2 mb-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-brand-champagne/40 hover:text-brand-champagne/70 group"
      >
        <span className="flex items-center gap-2">
          {HeaderIcon ? (
            <HeaderIcon className="sidebar-group-icon h-3 w-3 text-brand-champagne/45" aria-hidden />
          ) : null}
          <span>{label}</span>
        </span>
        <ChevronDown
          className={`sidebar-chevron h-3 w-3 ${collapsed ? "-rotate-90" : "rotate-0"}`}
          aria-hidden
        />
      </button>
      {!collapsed && (
        <nav id={listId} aria-labelledby={headerId} className="sidebar-stagger flex flex-col gap-0.5">
          {items.map((i) => (
            <SideLink
              key={i.to}
              to={i.to}
              end={i.to === "/dashboard"}
              label={i.label}
              Icon={i.icon}
              badge={badgeFor(i.to)}
            />
          ))}
        </nav>
      )}
    </div>
  );
}


export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const roleConfig = user && user.role
    ? NAV_BY_ROLE[user.role] || NAV_BY_ROLE.delegate
    : { top: [], groups: [] };
  // Flat list for mobile nav + any badge resolver below.
  const flatItems = useMemo(() => flattenNav(roleConfig), [roleConfig]);

  // Persisted collapse state per (role, group).
  const [collapsedGroups, setCollapsedGroups] = useState(() =>
    _loadCollapsedSet(user?.role),
  );
  // Re-load when the role changes (e.g. after onboarding sets organization_id).
  useEffect(() => {
    setCollapsedGroups(_loadCollapsedSet(user?.role));
  }, [user?.role]);

  // Auto-expand the group that contains the currently-active route so a
  // deep-link never strands the user inside a collapsed section they
  // can't see.
  useEffect(() => {
    if (!roleConfig?.groups) return;
    const activeGroup = roleConfig.groups.find((g) =>
      g.items.some((i) => location.pathname === i.to || location.pathname.startsWith(i.to + "/")),
    );
    if (activeGroup && collapsedGroups.has(activeGroup.id)) {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        next.delete(activeGroup.id);
        _saveCollapsedSet(user?.role, next);
        return next;
      });
    }
  }, [location.pathname, user?.role, roleConfig?.groups, collapsedGroups]);

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      _saveCollapsedSet(user?.role, next);
      return next;
    });
  }, [user?.role]);

  // R4B — Sidebar alert badge counters for super_admin only.
  const [alertCounters, setAlertCounters] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (user?.role !== "super_admin") return;
    const fetchCounters = async () => {
      try {
        const r = await api.get("/admin/diploks/reconciliation/alert-counters");
        if (!cancelled) setAlertCounters(r.data || null);
      } catch (_) {
        // silent — badges are non-critical
      }
    };
    fetchCounters();
    const id = setInterval(fetchCounters, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  const badgeFor = (to) => {
    if (user?.role !== "super_admin" || !alertCounters) return 0;
    if (to === "/dashboard/admin/diploks/reconciliation") {
      return alertCounters.total_attention_needed || 0;
    }
    return 0;
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const roleLabel = {
    super_admin: "Diplomexa Command",
    org_admin: "Organiser Console",
    event_manager: "Event Manager",
    chair: "Chair / Dais",
    school_admin: "School Console",
    faculty_advisor: "Faculty Advisor",
    volunteer: "Volunteer Ops",
    parent: "Parent Portal",
    delegate: "Delegate Portal",
    sponsor: "Sponsor Portal",
    judge: "Judge / Evaluator",
    partner_institution: "Partner Console",
  }[user?.role] || "Console";

  return (
    <div className="min-h-screen flex bg-[#f6f8fb]">
      {/* Sidebar — Institutional Luxury treatment */}
      <aside className="hidden lg:flex w-72 bg-brand-navy-dark flex-col border-r border-brand-gold/15 relative">
        {/* Foil stripe — gold hairline running down the right edge */}
        <div aria-hidden className="absolute top-0 right-0 bottom-0 w-px bg-gradient-to-b from-transparent via-brand-gold/30 to-transparent pointer-events-none" />
        <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
          <div className="brand-gleam rounded-md">
            <BrandLogo light to="/dashboard" />
          </div>
        </div>

        <div className="px-4 pt-5 pb-2 overflow-y-auto" data-testid="sidebar-nav">
          <div className="flex items-center justify-between px-2 mb-3">
            <p className="eyebrow text-brand-gold flex items-center gap-1.5">
              <Globe className="h-3 w-3 opacity-80 animate-gold-pulse" aria-hidden />
              {roleLabel}
            </p>
          </div>
          {/* Always-visible top items (Overview / My Child / Sponsor portal). */}
          {roleConfig.top && roleConfig.top.length > 0 && (
            <nav className="sidebar-stagger flex flex-col gap-0.5 mb-3" aria-label="Primary">
              {roleConfig.top.map((i) => (
                <SideLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/dashboard"}
                  label={i.label}
                  Icon={i.icon}
                  badge={badgeFor(i.to)}
                />
              ))}
            </nav>
          )}
          {/* Grouped sections — collapsible, persisted per role. */}
          {(roleConfig.groups || []).map((g) => (
            <SidebarGroup
              key={g.id}
              groupId={g.id}
              label={g.label}
              headerIcon={g.headerIcon}
              items={g.items}
              badgeFor={badgeFor}
              collapsed={collapsedGroups.has(g.id)}
              onToggle={() => toggleGroup(g.id)}
            />
          ))}
        </div>

        <div className="mt-auto px-4 py-4 border-t border-white/5">
          <Link
            to="/dashboard/profile"
            data-testid="sidebar-profile-link"
            className="flex items-center gap-3 px-2 py-2.5 rounded-sm bg-white/5 border border-brand-gold/10 hover:bg-white/10 hover:border-brand-gold/30 transition-all group"
            title="View your profile"
          >
            <div className="avatar-ring shrink-0">
              <img
                src={user?.avatar_url || "https://images.unsplash.com/photo-1645620592218-cf03f0d8c778"}
                alt=""
                className="h-9 w-9 rounded-full object-cover border border-brand-gold/40"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-brand-champagne truncate group-hover:text-brand-gold transition-colors">
                {user?.name || "—"}
              </p>
              <p className="text-xs text-brand-champagne/45 truncate">{user?.email}</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="btn-signout mt-3 w-full flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold py-2.5 rounded-sm text-brand-navy-dark shadow-gold-soft hover:shadow-gold-strong active:scale-[0.98]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Grace-mode sticky banner — silent unless org is expiring/in_grace/suspended */}
        <GraceModeBanner />
        {/* Topbar — glassmorphic with gold hairline */}
        <header className="glass sticky top-0 z-30">
          <div className="px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="lg:hidden">
              <BrandLogo to="/dashboard" />
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <TopBarTools user={user} />
              <DiploksLiveBalanceWidget />
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-sm bg-white border border-brand-gold/15">
                <span className="live-dot h-2 w-2 rounded-full bg-emerald-500 text-emerald-500 animate-gold-pulse" />
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-700">
                  {user?.role === "super_admin" ? "SYS OK" : "LIVE"}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile nav */}
        <div className="lg:hidden border-b border-slate-200 bg-white">
          <div className="flex overflow-x-auto no-scrollbar px-4 py-2 gap-2">
            {flatItems.map((i) => {
              const Icon = i.icon;
              return (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.to === "/dashboard"}
                  onClick={() => recordVisit(i.to)}
                  data-testid={`mobile-nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `shrink-0 flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold ${
                      isActive
                        ? "bg-[#0A192F] text-white"
                        : "bg-slate-100 text-slate-700"
                    }`
                  }
                >
                  <Icon className="h-3.5 w-3.5" />
                  {i.label}
                </NavLink>
              );
            })}
            <button
              onClick={handleLogout}
              className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold bg-[#D4AF37] text-[#0A192F]"
              data-testid="mobile-logout"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-10">{children}</main>

        {/* Sprint 2 — Diplomexa Welcome Splash + Smart Support */}
        <WelcomeSplash />
        <SmartSupportPanel />

        {/* Mandatory brand attribution inside every signed-in workspace */}
        <footer
          data-testid="dashboard-attribution"
          className="border-t border-slate-200/70 bg-white/70 backdrop-blur px-4 sm:px-6 lg:px-10 py-3 text-[11px] text-slate-500 flex flex-wrap items-center justify-between gap-2"
        >
          <p>
            Powered by Diplomexa by{" "}
            <a
              href="https://www.destineze.com"
              target="_blank"
              rel="noreferrer"
              className="text-slate-700 hover:text-[#0F4C81] underline-offset-2 hover:underline"
            >
              Destineze
            </a>{" "}
            and{" "}
            <a
              href="https://www.factskoot.com"
              target="_blank"
              rel="noreferrer"
              className="text-slate-700 hover:text-[#0F4C81] underline-offset-2 hover:underline"
            >
              Factskoot
            </a>
          </p>
          <p className="font-mono opacity-70">v3.0</p>
        </footer>
      </div>
    </div>
  );
}
